import { useIsWorkplaceReviewUXUpdateEnabled } from "@src/appV2/PostShiftReview/hooks/useIsWorkplaceReviewUXUpdateEnabled";
import { usePostComment } from "@src/appV2/redesign/Workplace/Review/api/usePostComment";
import { getWorkplaceCommentsQueryKey } from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { COMMENTS_PAGE_SIZE, LEGACY_COMMENTS_PAGE_SIZE } from "./constants";
import { useUpdateCachedComment } from "./useUpdateCachedComment";

interface UsePostReplyWithCacheUpdateProps {
  workplaceId: string;
  parentId: string;
}

export function usePostReplyWithCacheUpdate(props: UsePostReplyWithCacheUpdateProps) {
  const { workplaceId, parentId } = props;

  const [text, setText] = useState("");
  const queryClient = useQueryClient();
  const { isEnabled: isReviewUXUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();

  const commentsPageSize = isReviewUXUpdateEnabled ? COMMENTS_PAGE_SIZE : LEGACY_COMMENTS_PAGE_SIZE;

  const { updateComment: updatePaginatedCommentList } = useUpdateCachedComment({
    workplaceId,
    commentsParams: {
      workplaceId,
      page: { size: commentsPageSize },
      filter: isReviewUXUpdateEnabled ? { sort: "-relevancyScore" } : undefined,
    },
  });

  const { mutate: postReply, isLoading: isPostingReply } = usePostComment(workplaceId, {
    onSuccess: async (data) => {
      setText("");

      await queryClient.invalidateQueries({
        queryKey: getWorkplaceCommentsQueryKey({
          workplaceId,
          filter: { parentCommentId: parentId },
        }),
      });

      const parentComment = data.data.relationships.parentComment?.data;

      if (parentComment) {
        updatePaginatedCommentList(parentComment);
      }
    },
  });

  return { postReply, isPostingReply, text, setText };
}
